/**
 * AffDice SuperAdmin messages — the intro tour (`tour.ts` says which step opens which
 * screen).
 *
 *   `intro.*`   each step's title and words, and the "Open …" link
 *
 * The controls themselves (Skip tour, Exit tour, Next) are portal-kit's.
 */
import type { Catalogue } from '@affdice/portal-kit';

export const TOUR_MESSAGES = {
  'intro.go': { en: 'Open {screen}', es: 'Abrir {screen}', 'pt-BR': 'Abrir {screen}' },

  'intro.welcome.title': {
    en: 'Welcome to KD Affiliate SuperAdmin',
    es: 'Te damos la bienvenida a KD Affiliate SuperAdmin',
    'pt-BR': 'Boas-vindas ao KD Affiliate SuperAdmin',
  },
  'intro.welcome.body': {
    en:
      'This is the platform team’s console. It looks after every operator on the ' +
      'platform: their health, their customers and whether the records are intact. ' +
      'This short tour shows where each part lives.',
    es:
      'Esta es la consola del equipo de la plataforma. Supervisa a todos los ' +
      'operadores: su estado, sus clientes y si los registros siguen intactos. ' +
      'Este breve recorrido muestra dónde está cada parte.',
    'pt-BR':
      'Este é o console da equipe da plataforma. Ele acompanha todos os ' +
      'operadores: a saúde deles, os clientes e se os registros continuam ' +
      'íntegros. Este tour rápido mostra onde fica cada parte.',
  },

  'intro.overview.body': {
    en:
      'Start here each day: activity and billing across every operator, with ' +
      'the audit chain and the background consumers checked beside them.',
    es:
      'Empieza aquí cada día: la actividad y la facturación de todos los ' +
      'operadores, con la cadena de auditoría y los consumidores en segundo ' +
      'plano comprobados al lado.',
    'pt-BR':
      'Comece por aqui todo dia: a atividade e o faturamento de todos os ' +
      'operadores, com a cadeia de auditoria e os consumidores em segundo ' +
      'plano verificados ao lado.',
  },

  'intro.operators.body': {
    en:
      'Every operator on the platform. Add one, see which sign-ups are waiting ' +
      'for approval, and open an operator to look at its brands, team and ' +
      'settings. A change you make inside an operator’s data asks for a reason ' +
      'and the operator is told.',
    es:
      'Todos los operadores de la plataforma. Agrega uno, mira qué altas esperan ' +
      'aprobación y abre un operador para ver sus marcas, su equipo y su ' +
      'configuración. Un cambio que hagas en los datos de un operador pide un ' +
      'motivo y se le avisa al operador.',
    'pt-BR':
      'Todos os operadores da plataforma. Adicione um, veja quais cadastros ' +
      'aguardam aprovação e abra um operador para ver as marcas, a equipe e as ' +
      'configurações. Uma alteração feita nos dados de um operador pede um ' +
      'motivo e o operador é avisado.',
  },

  'intro.brands.body': {
    en:
      'Every brand across all operators, with its status, so a question about ' +
      'a brand can be answered without first finding its operator.',
    es:
      'Todas las marcas de todos los operadores, con su estado, para responder ' +
      'una pregunta sobre una marca sin buscar antes a su operador.',
    'pt-BR':
      'Todas as marcas de todos os operadores, com o status, para responder a ' +
      'uma pergunta sobre uma marca sem procurar antes o operador.',
  },

  'intro.affiliates.body': {
    en:
      'Affiliates’ names and statuses across operators, enough for support to ' +
      'answer a basic question. Their commercial terms stay with their operator.',
    es:
      'Nombres y estados de los afiliados de todos los operadores, lo justo para ' +
      'que soporte responda una pregunta básica. Sus condiciones comerciales ' +
      'quedan en manos de su operador.',
    'pt-BR':
      'Nomes e status dos afiliados de todos os operadores, o suficiente para o ' +
      'suporte responder a uma pergunta simples. As condições comerciais ficam ' +
      'com o operador.',
  },

  'intro.team.title': {
    en: 'Your team and what each role may do',
    es: 'Tu equipo y lo que puede hacer cada rol',
    'pt-BR': 'Sua equipe e o que cada função pode fazer',
  },
  'intro.team.body': {
    en:
      'Users is the platform’s own staff. Roles & permissions sets what each ' +
      'platform role may do, one capability at a time. An operator’s own team ' +
      'is theirs to manage, not ours.',
    es:
      'Usuarios es el personal de la propia plataforma. Roles y permisos define ' +
      'lo que puede hacer cada rol de la plataforma, capacidad por capacidad. ' +
      'El equipo de cada operador lo gestiona el propio operador.',
    'pt-BR':
      'Usuários é a equipe da própria plataforma. Funções e permissões define o ' +
      'que cada função da plataforma pode fazer, uma capacidade por vez. A ' +
      'equipe de cada operador é gerida pelo próprio operador.',
  },

  'intro.setup.title': {
    en: 'What every operator chooses from',
    es: 'Lo que eligen todos los operadores',
    'pt-BR': 'O que todo operador escolhe',
  },
  'intro.setup.body': {
    en:
      'Reference data holds the products, jurisdictions, currencies, devices and ' +
      'operating systems every operator picks from. Feature flags switch a ' +
      'feature on for the whole platform, with exceptions for single operators.',
    es:
      'Datos de referencia guarda los productos, jurisdicciones, monedas, ' +
      'dispositivos y sistemas operativos que eligen los operadores. Funciones ' +
      'activables enciende una función para toda la plataforma, con excepciones ' +
      'para operadores concretos.',
    'pt-BR':
      'Dados de referência guarda os produtos, jurisdições, moedas, dispositivos ' +
      'e sistemas operacionais que os operadores escolhem. Recursos ativáveis ' +
      'liga um recurso para toda a plataforma, com exceções para operadores ' +
      'específicos.',
  },

  'intro.integrity.title': {
    en: 'Problems that make no noise',
    es: 'Problemas que no hacen ruido',
    'pt-BR': 'Problemas que não fazem barulho',
  },
  'intro.integrity.body': {
    en:
      'Audit chain re-checks that no audit record has been changed or removed. ' +
      'Consumers shows each background job and when it last moved: a stalled ' +
      'one raises no error by itself, so this is where you find it.',
    es:
      'Cadena de auditoría vuelve a comprobar que ningún registro de auditoría ' +
      'se haya cambiado o borrado. Consumidores muestra cada proceso en segundo ' +
      'plano y cuándo avanzó por última vez: uno detenido no da ningún error por ' +
      'sí solo, así que aquí es donde se detecta.',
    'pt-BR':
      'Cadeia de auditoria verifica de novo que nenhum registro de auditoria foi ' +
      'alterado ou apagado. Consumidores mostra cada processo em segundo plano e ' +
      'quando ele andou pela última vez: um processo parado não gera erro ' +
      'sozinho, então é aqui que você o encontra.',
  },

  'intro.access.title': {
    en: 'Who looked, and who signed in as whom',
    es: 'Quién miró y quién entró como quién',
    'pt-BR': 'Quem olhou e quem entrou como quem',
  },
  'intro.access.body': {
    en:
      'Access log records each time platform staff opened an operator’s data. ' +
      'Log in as lists every session in which a staff member signed in as a ' +
      'customer, with the reason they gave.',
    es:
      'Registro de accesos anota cada vez que el personal de la plataforma abrió ' +
      'los datos de un operador. Iniciar sesión como lista cada sesión en la que ' +
      'alguien del equipo entró como un cliente, con el motivo que indicó.',
    'pt-BR':
      'Registro de acessos anota cada vez que a equipe da plataforma abriu os ' +
      'dados de um operador. Entrar como lista cada sessão em que alguém da ' +
      'equipe entrou como um cliente, com o motivo informado.',
  },

  'intro.done.title': {
    en: 'That’s the tour',
    es: 'Fin del recorrido',
    'pt-BR': 'Fim do tour',
  },
  'intro.done.body': {
    en:
      'Open it again any time from Platform tour at the foot of the left panel. ' +
      'When a new part of the console is added, that entry is highlighted until ' +
      'you open the tour again.',
    es:
      'Vuelve a abrirlo cuando quieras desde Recorrido por la plataforma, al pie ' +
      'del panel izquierdo. Cuando se añada una parte nueva a la consola, esa ' +
      'entrada se resaltará hasta que vuelvas a abrir el recorrido.',
    'pt-BR':
      'Abra de novo quando quiser em Tour pela plataforma, no rodapé do painel ' +
      'esquerdo. Quando uma parte nova for adicionada ao console, essa entrada ' +
      'fica destacada até você abrir o tour de novo.',
  },
} as const satisfies Catalogue;
